import type { DocumentReference, HumanName, Practitioner, Schedule } from '@medplum/fhirtypes';
import {
  NPI_IDENTIFIER_SYSTEM,
  SSN_IDENTIFIER_SYSTEM,
  WEEKLY_AVAILABILITY_EXT,
} from '@unify/fhir-model/constants';
import {
  getCaqhId,
  getCaqhPassword,
  getCaqhUsername,
  getClinical,
  getNuccTaxonomyCode,
  hasOnboardingAttestation,
} from '@unify/fhir-model/resources/practitioner';
import {
  DAY_OF_WEEK_CODES,
  type DayOfWeekCode,
  utcValueTimeToLocalWallTime,
} from '@unify/fhir-model/resources/schedule';
import type { OnboardingSectionId } from './onboardingTypes';

/**
 * Keep this file in sync with `medplum-server/src/bots/provider-onboarding-requirements.ts`.
 * When frontend onboarding completion requirements change here, update the backend bot requirements too.
 */

/** Align with calendar authoring default. */
export const PROVIDER_ONBOARDING_AVAILABILITY_TIMEZONE = 'America/Denver';

export interface ProviderOnboardingRelatedResources {
  driverLicenseDocument: DocumentReference | null;
  schedule: Schedule | null;
}

export interface ProviderOnboardingSectionResult {
  complete: boolean;
  missingFields: string[];
}

export type ProviderOnboardingSectionResults = Record<OnboardingSectionId, ProviderOnboardingSectionResult>;

function hasNonEmptyValue(value: string | undefined | null): boolean {
  return Boolean(value?.trim());
}

function digitsOnly(value: string | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

function getNameByUse(resource: Practitioner, use: HumanName['use']): HumanName | undefined {
  return resource.name?.find((name) => name.use === use);
}

function getIdentifierValue(resource: Practitioner, system: string): string {
  return resource.identifier?.find((identifier) => identifier.system === system)?.value ?? '';
}

function hasWorkPhone(resource: Practitioner): boolean {
  const phone = resource.telecom?.find((entry) => entry.system === 'phone' && entry.use === 'work')?.value;
  return digitsOnly(phone).length === 10;
}

function hasMailingAddress(resource: Practitioner): boolean {
  const address = resource.address?.[0];
  return Boolean(
    hasNonEmptyValue(address?.line?.[0]) &&
    hasNonEmptyValue(address?.city) &&
    hasNonEmptyValue(address?.state) &&
    digitsOnly(address?.postalCode).length === 5,
  );
}

function hasHeadshot(resource: Practitioner): boolean {
  return Boolean(resource.photo?.[0]);
}

function hasDriverLicenseDocument(documentReference: DocumentReference | null): boolean {
  return Boolean(documentReference?.content?.[0]?.attachment);
}

function hasLegalName(resource: Practitioner): boolean {
  const officialName = getNameByUse(resource, 'official') ?? resource.name?.[0];
  return Boolean(hasNonEmptyValue(officialName?.given?.[0]) && hasNonEmptyValue(officialName?.family));
}

function hasClinicalSelections(resource: Practitioner, key: 'specialties' | 'modalities' | 'ageGroups'): boolean {
  return Boolean(getClinical(resource)?.[key]?.length);
}

function sectionResult(missingFields: string[]): ProviderOnboardingSectionResult {
  return { complete: missingFields.length === 0, missingFields };
}

function isValidValueTime(valueTime: string | undefined): boolean {
  if (!valueTime) return false;
  const match = valueTime.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return false;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = match[3] !== undefined ? Number(match[3]) : 0;
  return hour <= 23 && minute <= 59 && second <= 59;
}

/**
 * Authoritative availability completeness for onboarding (mirrors server bot).
 * Walks raw weekly-availability extensions; rejects malformed/empty windows;
 * compares end > start in local wall time (America/Denver by default).
 */
export function evaluateAvailabilityScheduleMissingFields(
  schedule: Schedule | null | undefined,
  timeZone: string = PROVIDER_ONBOARDING_AVAILABILITY_TIMEZONE,
): string[] {
  if (!schedule) {
    return ['Weekly availability'];
  }
  if (schedule.active !== true) {
    return ['Active weekly schedule'];
  }

  let validWindowCount = 0;
  let hasMalformed = false;
  let hasInvalidRange = false;
  let sawWeeklyExtension = false;

  for (const ext of schedule.extension ?? []) {
    if (ext.url !== WEEKLY_AVAILABILITY_EXT) continue;
    sawWeeklyExtension = true;
    const dayCode = ext.extension?.find((e) => e.url === 'dayOfWeek')?.valueCode as DayOfWeekCode | undefined;
    if (!dayCode || !DAY_OF_WEEK_CODES.includes(dayCode)) {
      hasMalformed = true;
      continue;
    }
    const windowExts = ext.extension?.filter((e) => e.url === 'window') ?? [];
    if (windowExts.length === 0) {
      hasMalformed = true;
      continue;
    }
    for (const windowExt of windowExts) {
      const start = windowExt.extension?.find((e) => e.url === 'start')?.valueTime;
      const end = windowExt.extension?.find((e) => e.url === 'end')?.valueTime;
      if (!isValidValueTime(start) || !isValidValueTime(end)) {
        hasMalformed = true;
        continue;
      }
      const localStart = utcValueTimeToLocalWallTime(dayCode, start!, timeZone);
      const localEnd = utcValueTimeToLocalWallTime(dayCode, end!, timeZone);
      if (!localStart || !localEnd) {
        hasMalformed = true;
        continue;
      }
      const startM = localStart.hour * 60 + localStart.minute;
      const endM = localEnd.hour * 60 + localEnd.minute;
      if (endM <= startM) {
        hasInvalidRange = true;
        continue;
      }
      validWindowCount += 1;
    }
  }

  const missingFields: string[] = [];
  if (!sawWeeklyExtension || validWindowCount === 0) {
    missingFields.push('At least one working day');
  }
  if (hasMalformed) {
    missingFields.push('Valid availability time windows');
  }
  if (hasInvalidRange) {
    missingFields.push('End time after start time');
  }
  return missingFields;
}

export function getDefaultProviderOnboardingSectionResults(): ProviderOnboardingSectionResults {
  return {
    'personal-info': sectionResult([]),
    availability: sectionResult([]),
    profile: sectionResult([]),
    'licenses-education': sectionResult([]),
    attestation: sectionResult([]),
  };
}

export function evaluateProviderOnboardingSections(
  practitioner: Practitioner,
  relatedResources: ProviderOnboardingRelatedResources,
): ProviderOnboardingSectionResults {
  const personalInfoMissingFields = [
    ...(hasLegalName(practitioner) ? [] : ['Legal name']),
    ...(hasWorkPhone(practitioner) ? [] : ['Phone number']),
    ...(digitsOnly(getIdentifierValue(practitioner, NPI_IDENTIFIER_SYSTEM)).length === 10 ? [] : ['NPI']),
    ...(hasNonEmptyValue(getNuccTaxonomyCode(practitioner)) ? [] : ['NUCC taxonomy code']),
    ...(digitsOnly(getIdentifierValue(practitioner, SSN_IDENTIFIER_SYSTEM)).length === 9 ? [] : ['SSN']),
    ...(hasNonEmptyValue(practitioner.birthDate) ? [] : ['Date of birth']),
    ...(hasMailingAddress(practitioner) ? [] : ['Mailing address']),
    ...(hasHeadshot(practitioner) ? [] : ['Headshot']),
    ...(hasDriverLicenseDocument(relatedResources.driverLicenseDocument) ? [] : ["Driver's license"]),
  ];

  const availabilityMissingFields = evaluateAvailabilityScheduleMissingFields(relatedResources.schedule);

  const profileMissingFields = [
    ...(hasClinicalSelections(practitioner, 'specialties') ? [] : ['Clinical specialties']),
    ...(hasClinicalSelections(practitioner, 'modalities') ? [] : ['Treatment modalities']),
    ...(hasClinicalSelections(practitioner, 'ageGroups') ? [] : ['Preferred patient age groups']),
  ];

  const licensesEducationMissingFields = [
    ...(hasNonEmptyValue(getCaqhId(practitioner)) ? [] : ['CAQH ID']),
    ...(hasNonEmptyValue(getCaqhUsername(practitioner)) ? [] : ['CAQH username']),
    ...(hasNonEmptyValue(getCaqhPassword(practitioner)) ? [] : ['CAQH password']),
  ];

  const attestationMissingFields = hasOnboardingAttestation(practitioner) ? [] : ['Onboarding attestation'];

  return {
    'personal-info': sectionResult(personalInfoMissingFields),
    availability: sectionResult(availabilityMissingFields),
    profile: sectionResult(profileMissingFields),
    'licenses-education': sectionResult(licensesEducationMissingFields),
    attestation: sectionResult(attestationMissingFields),
  };
}

export function isAttestationUnlocked(results: ProviderOnboardingSectionResults): boolean {
  return (
    results['personal-info'].complete &&
    results.availability.complete &&
    results.profile.complete &&
    results['licenses-education'].complete
  );
}
